import CheckIcon from '@mui/icons-material/Check'
import { Box } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import eyedrop from '@/assets/images/enamelEyeDrop.svg'
import Button from '@/components/Button'
import Card from '@/components/Card'
import LabelWithTooltip from '@/components/LabelWithTooltip'
import Select from '@/components/Select'
import SnackbarTooltip from '@/components/SnackbarTooltip'
import type { Metadata } from '@/pages/stainingStudio/crown/StaingingStudio'
import { useGetMetadataQuery } from '@/store/apiService'
import {
  INTERNAL_EFFECTS_BACKGROUND,
  INTERNAL_EFFECTS_LABEL,
} from '@/utils/constants'
import { getOptions, sortAlphabetically } from '@/utils/helper'

// Offered by the metadata but rejected by every ceramic brand (422).
const HIDDEN_INTERNAL_EFFECTS = ['dec']

type Effect = {
  label: string
  value: string
}

export type InternalEffectPanelProps = {
  containerRef: React.RefObject<HTMLDivElement>
  ceramicBrand: string
  selectedInternalEffect: string
  onCeramicBrandChange: (value: string) => void
  onSelectedInternalEffectChange: (value: string) => void
}

function InternalEffectPanel({
  containerRef,
  ceramicBrand,
  selectedInternalEffect,
  onCeramicBrandChange,
  onSelectedInternalEffectChange,
}: InternalEffectPanelProps) {
  const { t } = useTranslation('translation', { keyPrefix: 'internalEffects' })
  const [snackbarOpen, setSnackbarOpen] = useState(false)

  const { data: metadata } = useGetMetadataQuery(undefined, {
    refetchOnMountOrArgChange: true,
  }) as {
    data: Metadata
    error: any
    isLoading: boolean
  }

  const options = useMemo(
    () => ({
      ceramicBrands: sortAlphabetically(
        getOptions(metadata, 'available_ceramic_brands'),
        'text',
      ),
      internalEffects: getOptions(metadata, 'available_effecttypes'),
    }),
    [metadata],
  )

  const { control } = useForm({
    defaultValues: {
      ceramicBrands: ceramicBrand,
    },
    mode: 'onChange',
  })

  useEffect(() => {
    if (!selectedInternalEffect) {
      setSnackbarOpen(false)
    }
  }, [selectedInternalEffect])

  return (
    <Box
      display="flex"
      flexDirection="column"
      flexGrow={1}
      gap={1}
      overflow="auto"
    >
      <Card>
        <Select
          options={options.ceramicBrands}
          title={t('ceramicBrand')}
          tooltipText={t('ceramicBrandTooltip')}
          control={control}
          name="ceramicBrands"
          onFormValueChange={(_name, value) => {
            onCeramicBrandChange(value)
          }}
          titleSx={{ fontSize: '18px', fontWeight: 400 }}
          placeholder="Select a Brand"
          optionDisabledChecker={(item) => {
            return !item.internal_effects_activated
          }}
        />
      </Card>

      <Card>
        <Box display="flex" flexDirection="column" gap={3}>
          <LabelWithTooltip
            label={t('matisseInternalEffects')}
            tooltipText={t('matisseInternalEffectsTooltip')}
            titleSx={{ fontSize: '18px', fontWeight: 400 }}
          />
          {options.internalEffects
            ?.filter(
              (effect: Effect) =>
                !HIDDEN_INTERNAL_EFFECTS.includes(effect.value),
            )
            ?.map((effect: Effect) => (
              <Box
                key={effect.value}
                display="flex"
                alignItems="center"
                gap={3}
              >
                <Button
                  variant="outlined"
                  size="small"
                  sx={{
                    alignSelf: 'flex-start',
                    fontSize: '15px',
                    cursor: 'pointer',
                    height: '36px',
                    width: '36px',
                    minWidth: '36px',
                    borderRadius: '4px',
                    lineHeight: 'normal',
                  }}
                  textTransform="uppercase"
                  onClick={() => {
                    onSelectedInternalEffectChange(
                      selectedInternalEffect === effect.value
                        ? ''
                        : effect.value,
                    )
                    setSnackbarOpen(true)
                  }}
                  label={
                    selectedInternalEffect === effect.value ? (
                      <CheckIcon fontSize="small" />
                    ) : (
                      <img src={eyedrop} alt="Select" width={18} height={18} />
                    )
                  }
                />
                <Button
                  label={INTERNAL_EFFECTS_LABEL[effect.value] ?? effect.value}
                  sx={{
                    color: 'black',
                    background: INTERNAL_EFFECTS_BACKGROUND[effect.value],
                    fontSize: '16px',
                    padding: '8px 10px',
                    flex: 1,
                    pointerEvents: 'none',
                    lineHeight: 'normal',
                  }}
                />
              </Box>
            ))}
        </Box>
      </Card>
      <SnackbarTooltip
        containerRef={containerRef}
        message="Click on the spot you want to measure."
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        placement="top"
        offset={[0, -100]}
        maxWidth={500}
        autoHideDuration={6000}
      />
    </Box>
  )
}

export default InternalEffectPanel
